import { computed, inject } from "@angular/core"
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals"
import { addEntities, addEntity, SelectEntityId, updateEntity, withEntities } from "@ngrx/signals/entities"
import { exhaustMap, firstValueFrom, map, of, pipe, switchMap, tap } from "rxjs"
import { DocumentService, FileUploadResponseDto } from "../../services/document/document.service"
import { UserFacade } from "../user/user.facade"
import { extractErrorMessage } from "../../interceptors/helpers/extract-error-message.helper"
import { rxMethod } from "@ngrx/signals/rxjs-interop"
import { tapResponse } from "@ngrx/operators"
import { 
    FileUploadService, 
    FileUploadDto as uploadDto,
    FileUploadResponseDto as uploadResponse 
} from "../../services/file-upload.service"


export interface CompleteFileUpload {
    s3_bucket: string
    object_key: string
}

export interface FileUpload {
    filename: string, 
    file: File, 
    callback?: () => void
}

export interface FileUploadToS3 {
    s3_presigned_url: string
    object_key: string
    s3_bucket: string
    content_type: string
    file: File
}

export interface ProviderDocument {
    document_id: number
    id?: number
    provider_id: number
    bucket_key: string
    object_key: string
    document_type_id: number
    created_at: string,
    active: boolean
}

const selectId: SelectEntityId<ProviderDocument> = (m) => Number((m as any).document_id ?? (m as any).id);

function normalizeProviderDocuments(response: any): ProviderDocument[] {
    const documents = Array.isArray(response)
        ? response
        : response?.documents ?? response?.provider_documents ?? response?.result ?? response?.data ?? [];

    if (!Array.isArray(documents)) {
        return [];
    }

    return documents
        .map((document: any) => ({
            ...document,
            document_id: Number(document?.document_id ?? document?.id ?? 0),
            id: Number(document?.id ?? document?.document_id ?? 0),
            bucket_key: document?.bucket_key ?? document?.bucket_name ?? document?.s3_bucket ?? '',
            object_key: document?.object_key ?? '',
            provider_id: Number(document?.provider_id ?? 0),
            document_type_id: Number(document?.document_type_id ?? 0),
            created_at: document?.created_at ?? '',
            updated_at: document?.updated_at ?? '',
            active: document?.active === true || document?.active === 1 || document?.active === '1'
        }))
        .filter((document: ProviderDocument) => Number(document.document_id) > 0);
}

export const DocumentStore = signalStore(
    {providedIn: 'root'},
    withEntities<ProviderDocument>(),
    withState({
        loading: false,
        documentCenterUploadStatus: 'idle' as 'idle' | 'loading' | 'success' | 'error',
        error: {
            error: false, message: ''
        }
    }),
    withComputed((store) => ({
        documents: computed(() => Object.values(store.entities()))
    })),
    withProps(() => ({
        documentService: inject(DocumentService)
    })),
    withMethods((store) => {
        const fileService = inject(FileUploadService)
        const documentService = inject(DocumentService)

        return {
            clearError() {
                patchState(store, {
                    error: {
                        error: false,
                        message: ''
                    }
                })
            },
            setError(error: boolean, message: string) {
                patchState(store, {
                    error: {
                        error, message
                    }
                })
            },
            setLoading(loading: boolean) {
                patchState(store, { loading })
            },
            update(id: number, changes: any) {
                patchState(store, updateEntity({id, changes}, { selectId }))
            },
            hasDriversLicense(provider_id: number) {
                const licenses = store.entities().find(
                    c => Number(c.provider_id) === Number(provider_id) && Number(c.document_type_id) === 3 && c.active
                ) ?? {} as ProviderDocument

                return licenses.document_id
            },
            hasProfessionalLicense(provider_id: number) {
                const licenses = store.entities().find(
                    c => Number(c.provider_id) === Number(provider_id) && Number(c.document_type_id) === 1 && c.active
                ) ?? {} as ProviderDocument
                return licenses.document_id
            },
            hasMalpracticeLicense(provider_id: number) {
                const licenses = store.entities().find(
                    c => Number(c.provider_id) === Number(provider_id) && Number(c.document_type_id) === 2 && c.active
                ) ?? {} as ProviderDocument
                return licenses.document_id
            },
            setDocumentCenterUploadStatus(documentCenterUploadStatus: 'idle' | 'loading' | 'success' | 'error') {
                patchState(store, {
                    documentCenterUploadStatus
                })
            },
            async completeDocumentUpload(clinic_id: number, provider_id: number, onboarding_status_id: number, callback?: () => void) {
                this.setLoading(true)
                this.clearError()
                try{
                    const success = await firstValueFrom(
                        documentService.completeDocumentUpload(clinic_id, provider_id, 5).pipe(
                            map((response: any) => response)
                        )
                    )

                    callback?.()                    
                } catch(err) {
                    this.setError(true, extractErrorMessage(err))
                } finally {
                    this.setLoading(false)
                }

            },
            async uploadFile(dto: uploadDto, file: File, callback?: () => void) {
                this.setLoading(true)
                this.clearError()
                try {
                    const existingDocument = store.entities().find(
                        (document) =>
                            Number(document.provider_id) === Number(dto.provider_id) &&
                            Number(document.document_type_id) === Number(dto.document_type_id) &&
                            document.active
                    )
                    const replacementDocumentId = Number(dto.document_id ?? dto.id ?? existingDocument?.document_id ?? existingDocument?.id ?? 0)
                    const requestDto = {
                        ...dto,
                        id: replacementDocumentId || dto.id,
                        document_id: replacementDocumentId || dto.document_id
                    }

                    const success: ProviderDocument = await firstValueFrom(
                        fileService.preFlightCommunicationFileUpload(requestDto).pipe(
                            switchMap((response: uploadResponse) => {
                            const { presigned_s3_link, object_key, bucket_name, content_type } = response;

                            return fileService.uploadFileToS3(presigned_s3_link, content_type, file).pipe(
                                switchMap(() =>
                                documentService.saveFile({
                                    object_key,
                                    bucket_key: bucket_name,
                                    id: replacementDocumentId || undefined,
                                    document_id: replacementDocumentId || undefined,
                                    provider_id: dto.provider_id,
                                    document_type_id: dto.document_type_id,
                                    licence_expire_date: (dto as any).licence_expire_date ?? (dto as any).license_expire_date ?? null,
                                    license_expire_date: (dto as any).license_expire_date ?? (dto as any).licence_expire_date ?? null,
                                    insurance_expire_date: (dto as any).insurance_expire_date ?? null,
                                }).pipe(
                                    map(() => ({
                                        object_key,
                                        bucket_key: bucket_name,
                                        provider_id: dto.provider_id,
                                        document_type_id: dto.document_type_id,
                                        licence_expire_date: (dto as any).licence_expire_date ?? (dto as any).license_expire_date ?? null,
                                        license_expire_date: (dto as any).license_expire_date ?? (dto as any).licence_expire_date ?? null,
                                        insurance_expire_date: (dto as any).insurance_expire_date ?? null,
                                        document_id: replacementDocumentId || Date.now(),
                                        id: replacementDocumentId || Date.now(),
                                        created_at: new Date().toISOString(),
                                        updated_at: new Date().toISOString(),
                                        active: true
                                    }))
                                )
                                )
                            );
                            })
                        )
                    );

                    if (replacementDocumentId) {
                        patchState(store, updateEntity({
                            id: replacementDocumentId,
                            changes: success
                        }, { selectId }))
                    } else {
                        patchState(store, addEntity(success, { selectId }))
                    }
                    callback?.();
                } catch (err) {
                    this.setError(true, extractErrorMessage(err))
                } finally {
                    this.setLoading(false)
                }
            },
            async getProviderDocuments() {
                
                const service = inject(DocumentService)
                const facade = inject(UserFacade)

                const check = Object.values(store.entities())
                if(check.length) return
                this.setLoading(true)
                try {
                    const response = await firstValueFrom(
                        service.getAllDocumentsByClinic(facade.clinic_id()).pipe(
                            map((response: any) => response)
                        )
                    )
                    const documents = normalizeProviderDocuments(response)
                    patchState(store, addEntities(documents, { selectId }))
                    
                } catch(err) {
                    this.setError(true, extractErrorMessage(err))
                } finally {
                    this.setLoading(false)
                }
            }
        }
    }),
    withMethods((store) => ({
        completeFileUpload: rxMethod<CompleteFileUpload>(
            pipe(
                tap(() => {
                    store.clearError();
                    store.setLoading(true);
                }),
                exhaustMap(({object_key, s3_bucket}) => {
                        return store.documentService.completeDocumentCenterUpload(s3_bucket, object_key).pipe(
                            tapResponse({
                                next: (response: any) => {
                                    patchState(store, addEntities([response]))
                                    store.setDocumentCenterUploadStatus("success")
                                },
                                error: (err: unknown) => {
                                    store.setError(true, extractErrorMessage(err))
                                    store.setDocumentCenterUploadStatus("error")
                                }
                            })
                        )
                    }
                )
            )
        ),
    })),
    withMethods((store) => ({
        uploadFileToS3: rxMethod<FileUploadToS3>(
            pipe(
                tap(() => {
                    store.clearError();
                    store.setLoading(true);
                }),
                exhaustMap(({s3_presigned_url, object_key, s3_bucket, file}) =>
                    store.documentService.uploadFileToS3(s3_presigned_url, file).pipe(
                        tapResponse({
                            next: (response: unknown) => {
                                store.completeFileUpload({s3_bucket, object_key})
                            },
                            error: (err: unknown) => {
                                store.setError(true, extractErrorMessage(err))
                                store.setDocumentCenterUploadStatus("error")
                            }
                        })
                    )
                )
            )
        ),
    })),
    withMethods((store) => ({
        uploadFileInit: rxMethod<File>(
            pipe(
                tap(() => {
                    store.clearError();
                    store.setDocumentCenterUploadStatus("loading");
                }),
                exhaustMap((file) => 
                    store.documentService.preFlightCommunicationFileUpload(file.name).pipe(
                        tapResponse({
                            next: (response: FileUploadResponseDto) => {
                                const { s3_presigned_url, object_key, s3_bucket, content_type } = response
                                store.uploadFileToS3({s3_presigned_url, content_type, object_key, s3_bucket, file})

                            },
                            error: (err: unknown) => {
                                store.setError(true, extractErrorMessage(err))
                                store.setDocumentCenterUploadStatus("error")
                            }
                        })
                    )
                )
            )
        ),
    }))
)
